import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, Output, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation } from "@angular/core";
import { GlobalTablesService } from "@common/services/global-tables-service";
import { PSTFieldNames } from '@common/data-models/fields/pst-field-names';
import { TopicRecord } from "@playbook/data-models/records/topic-record";
import { LanguageSettings } from "@common/services/language-settings";
import { PSTWEB_LIST_VALUE } from "@common/enums/constants";
import { EnpsRecordControl } from "@common/controls/enps-record-control/enps-record-control";
import { PlaybookService } from "@playbook/services/playbook-service";
import { AssignmentRecord } from "@src/suite/playbook/data-models/records/assignment-record";
import { ObjectTypes } from "@common/enums/object-types";
import { DateTime } from "@common/data-models/complex-types/date-time";
import { DateRange } from "@common/data-models/complex-types/date-range";
import { FieldDef } from "@common/data-models/service/field-def";
import { AppService } from "@suite/app.service";
import { FieldDefValidations } from "@common/enums/enums";
import { Table } from "../../data-models/complex-types/table";
import { MultiValue } from "../../data-models/complex-types/multi-value";
import { MatRipple } from "@angular/material/core";
import { CdkConnectedOverlay } from "@angular/cdk/overlay";
import { VisualStateService } from "../../services/visual-state-service";
import { DataStore } from "../../data-models/data-store/data-store";
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { StFullScreenDialog } from "../st-full-screen-dialog";
import { StExpansionPanel } from "../../components/st-expansion-panel/st-expansion-panel.component";
import { MatAccordion } from "@angular/material/expansion";
import { MatMenuTrigger } from "@angular/material/menu";
import { PlaybookSearchResults } from "@src/suite/playbook/data-models/playbook-search-results/playbook-search-results";
import { Subject, Subscription } from "rxjs";
import { LayoutState } from '@suite/common/services/layout-state';
import { Address } from "../../data-models/complex-types/address";
import { StTextAreaOverlay } from "../../controls/st-text-area/text-area-overlay/st-text-area-overlay";

@Component({
    selector: "topic-full-screen",
    templateUrl: "topic-full-screen.html",
    styleUrls: ["topic-full-screen.scss"],
    encapsulation: ViewEncapsulation.None,
    // changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: "topic-full-screen"
    }
})
export class TopicFullScreenDialogComponent {
    @Output() public close: EventEmitter<any> = new EventEmitter<any>();
    @Output() public onAssignmentOpen: EventEmitter<number> = new EventEmitter();
    @Output() public onAssignmentDelete: EventEmitter<number> = new EventEmitter();
    @Output() public onTopicOpen: EventEmitter<void> = new EventEmitter();
    pstFieldNames = PSTFieldNames;
    fieldDefValidations = FieldDefValidations;
    assignmentRecord: AssignmentRecord = new AssignmentRecord();
    isPanelOpen: boolean = false;
    topicEditable: boolean = true;
    selectedAssignmentIndex: number;
    assignmentsReadable: boolean = true;
    @ViewChild(MatRipple) ripple: MatRipple;
    @ViewChild('slug') slug: StTextAreaOverlay;
    @ViewChild('externalActions', { read: TemplateRef }) externalActions: TemplateRef<any>;
    @ViewChild(CdkConnectedOverlay) overlay: CdkConnectedOverlay;
    @ViewChild(MatAccordion) accordion: MatAccordion;
    @ViewChild(MatMenuTrigger) assignmentsTrigger: MatMenuTrigger;
    public dialogRef: MatDialogRef<StFullScreenDialog, any> = null;
    public shouldAddToGroup: boolean = false;
    recordChangedSubscription: Subscription = null;
    destroyed$ = new Subject<void>();

    private _isReadonly: boolean = false;
    public get isReadOnly(): boolean {
        return this._isReadonly;
    }
    public set isReadOnly(v: boolean) {
        if (!v && this.topicEditable) {
            this._isReadonly = v;
        } else if (v) {
            this._isReadonly = v;
        }
    }

    private _data: TopicRecord = null;
    public get data(): TopicRecord {
        return this._data;
    }
    public set data(v: TopicRecord) {
        this._data = v;
        if (v) {
            this.topicEditable = this._data?.getSecurityPrivsWrite(this.appService);
            this.assignmentsReadable = this.data?.getSecurityPrivsReadChildren(this.appService);
            this.isReadOnly = this.viewStateService?.inSlideshowMode
            if (this.data && this.data?.path && this.data?.path?.isWasteBinPath) {
                this.topicEditable = false;
                this.isReadOnly = true;
            }
            if (this.recordChangedSubscription) this.recordChangedSubscription.unsubscribe();
            this.recordChangedSubscription = this._data?.changeSubject.subscribe(() => this.recordChanged());
        }
    }

    constructor(
        public changeDetectorRef: ChangeDetectorRef,
        public globalTablesService: GlobalTablesService,
        public languageSettings: LanguageSettings,
        public playbookService: PlaybookService,
        public appService: AppService,
        private vref: ViewContainerRef,
        public visualStateService: VisualStateService,
        public viewStateService: LayoutState,
        protected dialog: MatDialog,
        @Inject(MAT_DIALOG_DATA) public inputData: any,
    ) {
        this.shouldAddToGroup = inputData.shouldAddToGroup;
    }

    async ngAfterViewInit() {
        this.vref.createEmbeddedView(this.externalActions);
        if(!this.data?.guid) this.slug.onContainerClick();
    }

    get isCreate(): boolean {
        return !this.data.guid;
    }

    get topicTypeTranslationLabelPrefix() {
        return `${PSTWEB_LIST_VALUE}_${PSTFieldNames.PSTTopicType}`;
    }

    get publicationDateLabel(): string {
        return this.data?.fileDate?.toISODateTimeString();
    }

    get timeUpdatedLabel(): string {
        return `${this.data?.modTime?.moment?.fromNow()}`;
    }

    get assignmentTypes(): { id: string, title: string }[] {
        return this.globalTablesService.getListItemsForField(this.appService.PSTFieldNames.PSTAssignmentCoverageType)
    }

    get coverageAssignmentsFiedGroupLabel() {
        const coverageAssignmentsFieldGroupLabel = 'PSTWebFieldGroup';
        return this.languageSettings.get(`${coverageAssignmentsFieldGroupLabel}_${this.languageSettings.PSTWebCoverageAssignmentsPane}`).includes(coverageAssignmentsFieldGroupLabel) ? this.languageSettings.PSTWebCoverageAssignmentsPane : this.languageSettings.get(`${coverageAssignmentsFieldGroupLabel}_${this.languageSettings.PSTWebCoverageAssignmentsPane}`)
    }

    get attachmentsFiedGroupLabel() {
        const attachmentsFieldGroupLabel = 'PSTWebFieldGroup';
        return this.languageSettings.get(`${attachmentsFieldGroupLabel}_${this.languageSettings.PSTWebAttachmentsPane}`).includes(attachmentsFieldGroupLabel) ? this.languageSettings.PSTWebAttachmentsPane : this.languageSettings.get(`${attachmentsFieldGroupLabel}_${this.languageSettings.PSTWebAttachmentsPane}`)
    }

    get showAttachments(): boolean {
        return this.appService.attachmentsAllowed && !!this.data?.guid;
    }

    get disabled(): boolean {
        return !this.data.isRequiredFieldsPopulated || this.loading || this.loadingSaveAndNext;
    }

    get enforceRequiredFieldsOnCreate(): boolean {
        return this.globalTablesService.EnforceRequiredFieldsOnCreate
    }

    get collapseAllLabel(): string {
        return this.languageSettings?.PSTWebCollapseAllLabel;
    }

    get expandAllLabel(): string {
        return this.languageSettings?.PSTWebExpandAllLabel;
    }


    get searchResults(): PlaybookSearchResults {
        return this.playbookService.playbookSearchResults;
    }

    get isRTL() {
        return this.globalTablesService.getRtl();
    }

    get topicDisabled(): boolean {
        return this.isReadOnly || !this.topicEditable;
    }

    get searchString(): string {
        return this.viewStateService.viewStateSearchQuery;
    }

    getItemLabel(item: any) {
        return this.languageSettings.get(`${this.topicTypeTranslationLabelPrefix}_${(item?.id) ? item?.title : item}`).includes(this.topicTypeTranslationLabelPrefix) ? ((item?.id) ? item?.title : item) : this.languageSettings.get(`${this.topicTypeTranslationLabelPrefix}_${(item?.id) ? item?.title : item}`);
    }


    getInnerProp(item: FieldDef) {
        const val = this.data?.get(item.ID.split('-')[0]);
        if (val) return val[item.ComplexPropInnerId]; else return '';
    }


    updateTopicAddresSubField(value: any, field: FieldDef, subFieldId: string) {
        if (this.isReadOnly) return;
        const fieldId = field?.ID?.split('-')?.[0];
        if (fieldId) {
            const field = this.data?.fieldDefsDict[fieldId];
            if (field?.Validation.toUpperCase() === FieldDefValidations.ADDRESS) {
                const address = this.data?.getAs<Address>(fieldId, new Address());
                address[subFieldId] = value;
                this.updateTopic(address, fieldId);
            }
        }
    }

    async onAssignmentOpenHandler(index: number) {
        this.onAssignmentOpen.emit(index);
    }


    async createAssignment(type: string) {
        this.assignmentsTrigger.closeMenu();
        try {
            if (this.data?.guid) {

                const path = this.data.path.fullPath + '\\R_' + this.data.guid;
                const database = this.data.database;
                const res = await this.playbookService.createNewAssignment(database, path, type, this.data, null);
                if (res && res.status) {
                    this.data = DataStore.byGuid<TopicRecord>(this.data.guid, null);
                    if (!this.data.hasChildWithGuid(res.data.guid)) {
                        this.data.addChildren(res.data);
                    }
                    this.onAssignmentOpenHandler(this.data.getChildren().length - 1);
                    await this.appService.basicContentService.updateBasicContent<TopicRecord | AssignmentRecord>(this.data.database, this.data.path.fullPath, this.data.guid, false, { "Type": this.data.type }, false, null);
                }
            } else {
                const assignment = new AssignmentRecord();
                await this.playbookService.initializeNewAssignmentRecord(assignment, this.data, type);
                this.data.addChildren(assignment);
                this.changeDetectorRef.detectChanges();
                this.data.checkRequiredProperties();
                this.onAssignmentOpenHandler(this.data.getChildren().length - 1);
            }
        } catch (ex) { }
    }


    recordChanged() {
        this.topicEditable = this.data?.getSecurityPrivsWrite(this.appService);
        this.assignmentsReadable = this.data?.getSecurityPrivsReadChildren(this.appService);
        this.isReadOnly = this.viewStateService.inSlideshowMode; // TODO - speak with Yanko about this
        if (this.data?.path?.isWasteBinPath) {
            this.topicEditable = false;
            this.isReadOnly = true;
        }
    }

    updateTopic(value: any, fieldId: string) {
        if (fieldId === PSTFieldNames.PSTTopicSlug) {
            this.data.topicSlug = value.trim();
        }
        if (fieldId == PSTFieldNames.PSTTopicEventStartDate) {
            const castedVal = value as DateTime;
            if (castedVal?.get() > this.data.getAs<DateTime>(PSTFieldNames.PSTTopicEventEndDate)?.get()) {
                EnpsRecordControl.saveChanges(new DateTime(castedVal?.get()), PSTFieldNames.PSTTopicEventEndDate, this.data);
            }
        }
        EnpsRecordControl.saveChanges(value, fieldId, this.data);
    }

    private getType(): string {
        let type: string = "Assignment";
        if (this.data.type == ObjectTypes.TOPIC) {
            type = "Topic";
        }
        if (this.data.type == ObjectTypes.ASSIGNMENT) {
            type = "Assignment";
        }
        if (this.data.type == ObjectTypes.DRAFT) {
            type = "Draft";
        }
        if (this.data.type == ObjectTypes.SCRIPT) {
            type = "Script";
        }
        if (this.data.type == ObjectTypes.WIRE) {
            type = "Wire";
        }
        return type;
    }

    public get groups(): Array<any> {
        return this.globalTablesService.getAllPropertyGroupsFor(this.getType());
    }

    public get specialProps(): Array<FieldDef> {
        return this.globalTablesService.getSpecialPropertiesFor(this.getType()).filter((field) => {
            return field.ID !== PSTFieldNames.PSTExternalFiles && field.ID !== PSTFieldNames.Relationships;
        });
    }

    @Output()
    public createEvent: EventEmitter<boolean> = new EventEmitter<boolean>();

    public loadingSaveAndNext: boolean = false;
    public loading: boolean = false;


    public get customCardItems(): FieldDef[] {
        return this.viewStateService.viewStateCustomCardItems;
    }

    public async create(event: boolean) {
        if (!this.data.isRequiredFieldsPopulated) return;
        event ? this.loadingSaveAndNext = true : this.loading = true;
        let assignments = this.data.getChildren();
        let res = await this.playbookService.saveNewTopic(this.data, null, assignments as AssignmentRecord[]);
        if (res.status) {
            if (this.shouldAddToGroup) {
                this.playbookService.playbookSearchResults.addTopic(res.data);
            } else {
                const searchResults = this.playbookService.playbookSearchResults;
                searchResults.newGuids[res.data?.guid] = true;
                searchResults.newItemsCount++;
                this.searchResults.insertAfterTopic(
                    PlaybookSearchResults.newlyAddedItems.items[PlaybookSearchResults.newlyAddedItems.items.length - 1] as TopicRecord,
                    res.data,
                    true
                )
            }
            this.createEvent.emit(event);
            this.close.next(res);
            this.loading = false
        } else {
            this.loading = false
            // TODO: error handling
        }
    }

    public isGroupSingleValue(property: string): boolean {
        return this.globalTablesService.isGroupSingleValue && (property == PSTFieldNames.PSTAssignmentPublishingDesk || property == PSTFieldNames.PSTTopicDepartment);
    }

    public getDateTimeValue(id: string): DateTime {
        return this.data.getAs<DateTime>(id, new DateTime());
    }

    public getDateRangeValue(id: string): DateRange {
        return this.data.getAs<DateRange>(id, new DateRange());
    }

    getMinDate(id: string): DateTime {
        if (id == PSTFieldNames.PSTTopicEventEndDate) {
            return this.data.getAs<DateTime>(PSTFieldNames.PSTTopicEventStartDate);
        }
    }

    public getStaffIdValue(id: string): MultiValue<string> {
        return this.data.getAs<MultiValue<string>>(id, new MultiValue<string>());
    }

    public getCheckListValue(id: string): MultiValue<string> {
        return this.data.getAs<MultiValue<string>>(id, new MultiValue<string>());
    }

    public getList(group: string): Array<FieldDef> {
        return this.globalTablesService.getDynamicPropertiesFor(this.getType(), group).filter(p => p.ID !== PSTFieldNames.PSTTeamLocation);
    }

    public getFieldGroupsLabel(group: any) {
        const fieldGroupLabel = 'PSTWebFieldGroup';
        return this.languageSettings.get(`${fieldGroupLabel}_${group.name == '' ? 'Properties' : group.name}`).includes(fieldGroupLabel) ? (group.name == '' ? 'Properties' : group.name) : this.languageSettings.get(`${fieldGroupLabel}_${group.name == '' ? 'Properties' : group.name}`);
    }

    public getValue(id: string, validation: string): any {

        if (!this.data.get(id)) {
            if ((validation.toUpperCase() == FieldDefValidations.SPECIAL) || (validation.toUpperCase() == FieldDefValidations.COST)) {
                this.data.set(id, new Table());
            }
        }
        const value = this.data.getAs<Table>(id);
        value.colMetadata = this.globalTablesService.getListColumnsForSpecialField(id);
        value.addEmptyRowIfNeeded();
        return value;
    }

    public onMouseOut(event: MouseEvent) {
        if (this.visualStateService.isTouchDevice) return;
        if (!(event.relatedTarget as HTMLElement)?.classList?.contains('st-external-actions-overlay')) {
            this.isPanelOpen = false;
        }
    }

    externalActionsMouseEnter(event: MouseEvent) {
        if (this.data?.guid) {
            this.isPanelOpen = true;
        }
    }

    panelAttached() {
        const overlayElement = this.overlay?.overlayRef?.overlayElement;
        if (overlayElement) {
            overlayElement.addEventListener("mouseleave", (e) => {
                this.isPanelOpen = false;
            });
        }
    }

    expandCollapseAll(paneContainer: StExpansionPanel) {
        if (paneContainer.expanded) {
            this.accordion.closeAll();
        } else {
            this.accordion.openAll();
        }
    }

    panelStateChanged($event: boolean, location: string, title: string) {
        this.visualStateService.saveExpanderSettings(location, title, $event);
    }
}
